import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import { managementActions } from "../action-creators";
import type { SalesforceExportState } from "../type";

const SALES_FORCE_EXPORT_SETTINGS_STATE: SalesforceExportState = {
    settings: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    columns: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    isColumnsLoading: false,
    isLoadingSettings: false,
    prevSettings: {
        accounts: [],
        contacts: [],
        leads: [],
        companyAsLeads: [],
    },
    settingDeleteError: undefined,
    settingsCreateError: undefined,
    settingsFetchError: undefined,
    isSavingSettings: false,
    isSavedSettings: false,
    saveSettingsError: undefined,
    editableField: undefined,
    deletableField: undefined,
    exportingDomainsToSF: false,
    exportingDomainsToSFError: undefined,
    showModalDisabledExportSF: false,
    notification: undefined,
    settingExportAccountsStatus: false,
    exportAccountsStatusError: undefined,
};

export const salesforceExportReducer = createReducer<
    SalesforceExportState,
    ActionType<typeof managementActions.salesforce>
>(SALES_FORCE_EXPORT_SETTINGS_STATE)
    .handleAction(managementActions.salesforce.setSfDeletableField, (state, { payload }) => ({
        ...state,
        deletableField: payload,
    }))
    .handleAction(managementActions.salesforce.fetchSFExportSettingsTableAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: {
            ...state.prevSettings,
            [payload.typeName]: payload.settings,
        },
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        isLoadingSettings: false,
    }))
    .handleAction(managementActions.salesforce.fetchSFExportSettingsTableAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(managementActions.salesforce.fetchSFExportSettingsTableAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingSettings: false,
        settingsFetchError: payload,
    }))
    .handleAction(managementActions.salesforce.fetchSfExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: payload,
        settings: payload,
        isLoadingSettings: false,
    }))
    .handleAction(managementActions.salesforce.fetchSfExportSettingsAsync.request, (state) => ({
        ...state,
        isLoadingSettings: true,
    }))
    .handleAction(managementActions.salesforce.fetchSfExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingSettings: false,
        settingsFetchError: payload,
    }))
    .handleAction(managementActions.salesforce.saveSFExportSettingsAsync.success, (state, { payload }) => ({
        ...state,
        prevSettings: {
            ...state.prevSettings,
            [payload.typeName]: payload.settings,
        },
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        isSavingSettings: false,
        isSavedSettings: true,
    }))
    .handleAction(managementActions.salesforce.saveSFExportSettingsAsync.request, (state) => ({
        ...state,
        isSavingSettings: true,
    }))
    .handleAction(managementActions.salesforce.saveSFExportSettingsAsync.failure, (state, { payload }) => ({
        ...state,
        isSavingSettings: false,
        saveSettingsError: payload,
    }))
    .handleAction(managementActions.salesforce.exportDomainsToSFAsync.success, (state) => ({
        ...state,
        exportingDomainsToSF: false,
    }))
    .handleAction(managementActions.salesforce.exportDomainsToSFAsync.request, (state) => ({
        ...state,
        exportingDomainsToSF: true,
    }))
    .handleAction(managementActions.salesforce.exportDomainsToSFAsync.failure, (state, { payload }) => ({
        ...state,
        exportingDomainsToSF: false,
        exportingDomainsToSFError: payload,
    }))
    .handleAction(managementActions.salesforce.showModalDisabledExportSF, (state, { payload }) => ({
        ...state,
        showModalDisabledExportSF: payload,
    }))
    .handleAction(managementActions.salesforce.setSfNotification, (state, { payload }) => ({
        ...state,
        notification: payload,
    }))
    .handleAction(managementActions.salesforce.addNewSettingsExport, (state, { payload: { typeName, settings } }) => ({
        ...state,
        settings: {
            ...state.settings,
            [typeName]: [...state.settings[typeName], ...settings],
        },
    }))
    .handleAction(managementActions.salesforce.updateSfSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.salesforce.deleteSettingExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
        deletableField: undefined,
    }))
    .handleAction(managementActions.salesforce.revertSettingsExport, (state, { payload }) => ({
        ...state,
        settings: {
            ...state.settings,
            [payload.typeName]: payload.settings,
        },
    }))
    .handleAction(managementActions.salesforce.getSfColumnsAsync.request, (state) => ({
        ...state,
        isColumnsLoading: true,
    }))
    .handleAction(managementActions.salesforce.getSfColumnsAsync.success, (state, { payload }) => ({
        ...state,
        columns: payload,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.salesforce.getSfColumnsAsync.failure, (state) => ({
        ...state,
        isColumnsLoading: false,
    }))
    .handleAction(managementActions.salesforce.saveSfAllSettingsError, (state, { payload }) => ({
        ...state,
        saveSettingsError: payload,
    }));
